import type React from 'react';
import moment from 'moment-timezone';
import { isEqual } from 'lodash-es';
import { type SchedulerDetailsTypes } from '@/components/shared/interview-scheduler/SchedulerDetails/SchedulerDetails.types';
import { useDateFormat, useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import {
  OVERRIDE_TYPE,
  getOverrideType,
  hasOverride,
} from '@/components/shared/interview-scheduler/SchedulerDetails/overrideType.constants';

// TODO: we have changed the logic to check for custom slots, so this function is not needed anymore

// function checkForCustomSlots(
//   schedulerState: Record<string, any> = {}
// ): boolean {
//   let userCreatedCustomSlots = false;

//   // Loop through each week in the scheduler state
//   for (const weekKey in schedulerState) {
//     const weekData = schedulerState[weekKey];

//     // Add null safety checks
//     if (!weekData?.days) continue;

//     // Loop through each day in the current week
//     for (const day in weekData.days) {
//       const dayData = weekData.days[day];

//       if (!dayData) continue;

//       // Check if day has custom slots
//       if (dayData.customSlots?.length > 0) {
//         // Simply check if any slot has is_custom = true
//         const hasUserCreatedSlots = dayData.customSlots.some((slot: any) => {
//           return slot.is_custom === true;
//         });

//         if (hasUserCreatedSlots) {
//           userCreatedCustomSlots = true;
//         }
//       }
//     }

//     // If found user-created custom slots, break out early
//     if (userCreatedCustomSlots) break;
//   }

//   // Return true only if there are genuine user-created custom slots
//   // If user only selected checkboxes/fixed time, this should return false
//   return userCreatedCustomSlots;
// }

// todo: BE expects in this format
const nextWeekMap = {
  '1': 'Next weekday',
  '2': 'Next 2 weekdays',
  '3': 'Next 3 weekdays',
  '4': 'Next 4 weekdays',
  '5': 'Next 5 weekdays',
  next_week: 'Next week',
};

export const useSchedulerDetailsPayload = ({
  interviewDataRef,
  interviewPlan,
  isEdit,
  formRef,
  variant,
  contacts,
  previewDetailsRef,
  formOnly = false,
}: {
  interviewDataRef: React.MutableRefObject<SchedulerDetailsTypes.InterviewData>;
  interviewPlan: SchedulerDetailsTypes.SchedulerDetailsProps['interviewPlan'];
  isEdit: boolean;
  variant: string;
  contacts: any[];
  formRef: React.MutableRefObject<any>;
  previewDetailsRef: React.MutableRefObject<any>;
  formOnly?: boolean;
}) => {
  const toast = useShowToast();
  const { dateFormat } = useDateFormat();
  const { feOverrideType } = useKFlags();

  const getManualScheduleData = () => {
    const { bookedSlot, datesSet } = interviewDataRef.current || {};
    const { start: start_time, end: end_time } = bookedSlot || {};

    const hasValidData = !!start_time;
    const schedule_slots_modified = !isEdit
      ? true
      : interviewPlan?.start_time !== start_time;
    const schedule_type_modified = !isEdit ? true : false;

    if (!formOnly && !start_time) {
      toast({
        title: 'Select a time slot to schedule the interview',
        status: 'error',
      });
      return { isBreak: true };
    }

    if (formOnly) {
      return {
        manualValues: {},
        hasValidData: true,
        schedule_slots_modified: true,
        schedule_type_modified: true,
      };
    }

    const isTimeInValid = moment
      .tz(start_time, datesSet?.timeZone)
      .isBefore(moment.tz(datesSet?.timeZone));
    if (isTimeInValid) {
      toast({ title: 'Selected Event slot is in past', status: 'error' });
      return { isBreak: true };
    }

    return {
      manualValues: { end_time, start_time },
      hasValidData,
      schedule_slots_modified,
      schedule_type_modified,
    };
  };

  const getSelfScheduleData = () => {
    const {
      schedulerState,
      datesSet,
      currentWeek,
      userEventsInfo,
      roomEventsInfo,
      interviewers,
      meetingRooms,
      interviewKind,
    } = interviewDataRef.current || {};

    const isOneOnOne = interviewKind === 'one_on_one';

    // Helper function to check if a slot overlaps with any busy event
    const isSlotOverlappingBusyEvent = (
      slot: any,
      busyEvents: any[]
    ): boolean => {
      const slotStart = moment(slot.start);
      const slotEnd = moment(slot.end);

      return busyEvents.some((event: any) => {
        const eventStart = moment(event.start_time || event.start);
        const eventEnd = moment(event.end_time || event.end);
        return slotStart.isBefore(eventEnd) && slotEnd.isAfter(eventStart);
      });
    };

    // Helper function to check if a slot is within a single interviewer's availability
    const isSlotWithinInterviewerAvailability = (
      slot: any,
      availabilityPeriods: any[]
    ): boolean => {
      if (!availabilityPeriods || availabilityPeriods?.length === 0) {
        return false;
      }

      const slotStart = moment(slot?.start);
      const slotEnd = moment(slot?.end);

      return availabilityPeriods?.some((period) => {
        const periodStart = moment(period?.start);
        const periodEnd = moment(period?.end);
        return (
          slotStart.isSameOrAfter(periodStart) &&
          slotEnd.isSameOrBefore(periodEnd)
        );
      });
    };

    // Helper function to check if a slot is outside working hours
    // For panel: slot must be within ALL interviewers' availability (intersection)
    // For one-on-one: slot must be within at least ONE interviewer's availability (union)
    const isSlotOutsideWorkingHours = (
      slot: any,
      availabilityByInterviewer: Record<string, any[]>,
      interviewerIds: any[]
    ): boolean => {
      if (!interviewerIds || interviewerIds.length === 0) {
        return true;
      }

      if (isOneOnOne) {
        // ONE-ON-ONE: Slot is outside working hours if NO interviewer has it available
        const anyInterviewerHasAvailability = interviewerIds?.some(
          (interviewerId) => {
            const availability =
              availabilityByInterviewer[String(interviewerId)] || [];
            return isSlotWithinInterviewerAvailability(slot, availability);
          }
        );
        return !anyInterviewerHasAvailability;
      } else {
        // PANEL: Slot is outside working hours if ANY interviewer doesn't have it available
        const allInterviewersHaveAvailability = interviewerIds?.every(
          (interviewerId) => {
            const availability =
              availabilityByInterviewer[String(interviewerId)] || [];
            return isSlotWithinInterviewerAvailability(slot, availability);
          }
        );
        return !allInterviewersHaveAvailability;
      }
    };

    // Collect all busy events from selected interviewers and meeting rooms
    const allBusyEvents: any[] = [];
    // Collect availability per interviewer for proper intersection/union logic
    const availabilityByInterviewer: Record<string, any[]> = {};

    if (userEventsInfo && interviewers) {
      Object.values(userEventsInfo).forEach((weekData: any) => {
        interviewers.forEach((interviewerId: any) => {
          const interviewerData = weekData[String(interviewerId)];
          if (interviewerData?.busyEvents) {
            allBusyEvents.push(...interviewerData.busyEvents);
          }
          if (interviewerData?.availability) {
            if (!availabilityByInterviewer[String(interviewerId)]) {
              availabilityByInterviewer[String(interviewerId)] = [];
            }
            availabilityByInterviewer[String(interviewerId)]?.push(
              ...interviewerData.availability
            );
          }
        });
      });
    }

    if (roomEventsInfo && meetingRooms) {
      Object.values(roomEventsInfo).forEach((weekData: any) => {
        meetingRooms.forEach((roomId: any) => {
          const roomData = weekData[String(roomId)];
          if (roomData?.busyEvents) {
            allBusyEvents.push(...roomData.busyEvents);
          }
        });
      });
    }

    // Collect all selected time slots from scheduler state
    const time_slots: any[] = [];

    Object.values(schedulerState).forEach((week: any) => {
      if (!week?.days) return;
      Object.values(week.days).forEach((day: any) => {
        if (day?.isSelected) {
          const validGeneratedSlots = day.slotsGenerated.filter(
            (slot: any) => !day.removedSlots.includes(slot.id)
          );
          const validCustomSlots = day.customSlots.filter((slot: any) => {
            const cleanedId = slot.id.split('custom')[1];

            // If slot ID is in removedSlots, exclude it (return false)
            if (day.removedSlots.includes(cleanedId)) return false;

            // Check if there's a duplicate in generated slots
            const isDuplicate = day.slotsGenerated.find(
              (i: any) => i?.id === cleanedId
            );

            return !isDuplicate;
          });

          time_slots.push(
            ...validGeneratedSlots.map((slot: any) => ({ ...slot })),
            ...validCustomSlots.map((slot: any) => ({
              ...slot,
              _isManualSlot: true,
            }))
          );
        } else {
          time_slots.push(
            ...day.customSlots.map((slot: any) => ({
              ...slot,
              _isManualSlot: true,
            }))
          );
        }
      });
    });

    // Set override_type (or is_custom for backward compatibility) only for manually created slots
    time_slots.forEach((slot: any) => {
      if (!slot._isManualSlot) return;

      // Skip re-evaluation for server-loaded slots
      if (slot._fromServer) return;

      const hasExistingOverride =
        slot.override_type && slot.override_type !== OVERRIDE_TYPE.NONE;
      if (!hasExistingOverride) {
        const overlapsWithBusyEvent = isSlotOverlappingBusyEvent(
          slot,
          allBusyEvents
        );
        const outsideWorkingHours = isSlotOutsideWorkingHours(
          slot,
          availabilityByInterviewer,
          interviewers
        );

        if (feOverrideType) {
          slot.override_type = getOverrideType(
            overlapsWithBusyEvent,
            outsideWorkingHours
          );
          // TODO: Remove this after BE changes
          slot.is_custom = hasOverride(slot.override_type);
        } else {
          slot.is_custom = overlapsWithBusyEvent || outsideWorkingHours;
        }
      } else {
        if (feOverrideType) {
          slot.is_custom = hasOverride(slot.override_type);
        }
      }
    });

    const hasValidData = time_slots.length > 0;
    const schedule_slots_modified = !isEqual(
      time_slots,
      interviewPlan?.time_slots
    );

    const currentDay = moment.tz(datesSet?.timeZone).format(dateFormat?.label);
    const {
      customSlots = [],
      slotsGenerated = [],
      removedSlots,
    } = schedulerState[currentWeek]?.days?.[currentDay] ?? {};

    const filteredSlotsGenerated = slotsGenerated.filter(
      (slot: any) => !removedSlots.includes(slot.id)
    );
    const isSlotsInPast = [...filteredSlotsGenerated, ...customSlots].some(
      (slot: any) =>
        moment.tz(slot?.start, datesSet?.timeZone) <
        moment.tz(datesSet?.timeZone)
    );

    if (isSlotsInPast) {
      toast({ title: 'All slots should be in the future', status: 'error' });
      return { isBreak: true };
    }

    if (time_slots.length === 0) {
      const isBulkSchedule = variant === 'bulk-self' && contacts.length > 1;
      toast({
        title: `Select time slots to send to the candidate${
          isBulkSchedule ? 's' : ''
        }`,
        status: 'error',
      });
      return { isBreak: true };
    }
    const { findTimeValue } = interviewDataRef.current || {};
    const hasCustomSlots = feOverrideType
      ? time_slots.some(
          (s) =>
            s._isManualSlot &&
            hasOverride(s.override_type ?? OVERRIDE_TYPE.NONE)
        )
      : time_slots.some((s) => s._isManualSlot && s.is_custom === true);
    const schedule_type = hasCustomSlots
      ? 'Custom'
      : (nextWeekMap[String(findTimeValue)] ?? 'Custom');

    const schedule_type_modified =
      previewDetailsRef.current?.schedule_type_modified !== schedule_type;

    return {
      selfValues: {
        schedule_type,
        time_slots: time_slots.map((slot) => {
          const baseSlot = {
            start_at: slot.start,
            end_at: slot.end,
          };

          if (slot._isManualSlot) {
            if (feOverrideType) {
              return {
                ...baseSlot,
                override_type: slot.override_type ?? OVERRIDE_TYPE.NONE,
                // TODO: Remove this after BE changes
                is_custom: slot.is_custom,
              };
            } else {
              return {
                ...baseSlot,
                is_custom: slot.is_custom,
              };
            }
          }
          return baseSlot;
        }),
      },
      hasValidData,
      schedule_slots_modified,
      schedule_type_modified,
    };
  };

  const getSchedularDetailsModel = async () => {
    await formRef.current?.submitForm();
    await new Promise((resolve) => setTimeout(resolve, 50));

    const { isValid, values, errors } = formRef.current || {};

    // Additional validation for onsite interviews
    if (values?.location === 'onsite' && !values?.ats_office_id) {
      const element = document?.getElementById('ats_office_id');
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    // For manual mode (including bulk-manual), if we have startTime/endTime errors, check if it's because no slot is booked
    // and show a more helpful message (skip when formOnly - no slot required)
    if (
      !formOnly &&
      (variant === 'manual' || variant === 'bulk-manual') &&
      errors?.startTime &&
      errors?.endTime
    ) {
      const { bookedSlot } = interviewDataRef.current || {};
      const { start: start_time } = bookedSlot || {};

      if (!start_time) {
        toast({
          title: 'Select a time slot to schedule the interview',
          status: 'error',
        });
        return;
      }
    }

    // When formOnly, ignore slot/calendar-related errors (not required when no calendar)
    const SLOT_ERROR_KEYS = ['startTime', 'endTime', 'startDate', 'endDate'];
    const errorsToCheck = formOnly
      ? Object.fromEntries(
          Object.entries(errors || {}).filter(
            ([k]) => !SLOT_ERROR_KEYS.includes(k)
          )
        )
      : errors || {};
    const [key] = Object.entries(errorsToCheck)[0] || [];
    if (key) {
      const element = document?.getElementById(key);
      element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const {
      bookedSlot,
      datesSet,
      meetingRoomObj,
      interviewersDetails,
      calendar_visibility,
      calendar_info,
    } = interviewDataRef.current || ({} as any);
    const { duration: slotDuration } = bookedSlot || {};
    const timezone =
      datesSet?.timeZone || (formOnly ? moment.tz.guess() : undefined);
    const duration = formOnly
      ? (values?.duration ?? slotDuration)
      : slotDuration;
    const {
      name,
      kind,
      host_id,
      location,
      note_taker_enabled,
      note_taker_mandatory,
      interviewers,
      meeting_rooms_enabled,
      meeting_room_ids,
      ats_office_id,
      ats_office,
      ext_interview_template_id,
      fill_with_ai_enabled,
    } = values ?? {};

    let isBreak, hasValidData, slotModified, typeModified, calendarValues;

    const generateMeetingRoomsPayload = () => {
      const { meeting_rooms = [] } = interviewPlan || {};

      // If meeting rooms are disabled, mark all server meeting rooms for deletion
      if (!meeting_rooms_enabled) {
        return meeting_rooms.map((room) => ({
          ...room,
          _destroy: true,
        }));
      }

      const serverMeetingRoomIds = meeting_rooms.map((room) => room.email);

      const meetingRoomIds = Array.isArray(meeting_room_ids)
        ? meeting_room_ids
        : [meeting_room_ids];

      // Find new meeting rooms (in meeting_room_ids but not in server data)
      const newMeetingRoomPayload = meetingRoomIds
        ?.filter((id) => !serverMeetingRoomIds.includes(id))
        .map((id) => ({
          name: meetingRoomObj[id],
          email: id,
        }));

      // Find deleted rooms (in server data but not in meeting_room_ids)
      const deletedMeetingRoomsPayload = meeting_rooms
        .filter((room) => !meetingRoomIds.includes(room.email))
        .map((room) => ({
          ...room,
          _destroy: true,
        }));

      // Return combination of new rooms and deleted rooms with _destroy flag
      return [...newMeetingRoomPayload, ...deletedMeetingRoomsPayload];
    };

    if (variant === 'manual' || variant === 'bulk-manual') {
      const {
        isBreak: breakFunc,
        manualValues,
        hasValidData: valid,
        schedule_slots_modified,
        schedule_type_modified,
      } = getManualScheduleData();
      isBreak = breakFunc;
      hasValidData = valid;
      slotModified = schedule_slots_modified;
      typeModified = schedule_type_modified;
      calendarValues = manualValues;
    } else {
      const {
        isBreak: breakFunc,
        selfValues,
        hasValidData: valid,
        schedule_slots_modified,
        schedule_type_modified,
      } = getSelfScheduleData();
      isBreak = breakFunc;
      hasValidData = valid;
      slotModified = schedule_slots_modified;
      typeModified = schedule_type_modified;
      calendarValues = selfValues;
    }

    if (isBreak) {
      return;
    }

    const hasCalendarInfo =
      calendar_info &&
      typeof calendar_info === 'object' &&
      Object.keys(calendar_info).length > 0;

    const payload = {
      name,
      kind,
      location,
      note_taker_enabled,
      note_taker_mandatory,
      ats_office_id,
      ats_office,
      ext_interview_template_id,
      fill_with_ai_enabled,
      participant_ids: Array.isArray(interviewers)
        ? interviewers
        : [interviewers],
      meeting_rooms_attributes: generateMeetingRoomsPayload(),
      duration,
      ...calendarValues,
      timezone,
      schedule_slots_modified: slotModified,
      schedule_type_modified: typeModified,
      calendar_visibility,
      ...((interviewPlan as any)?.shared_calendar_enabled && hasCalendarInfo
        ? { calendar_info }
        : {}),
    };

    if (location === 'zoom') {
      payload.host_id = host_id;
    }

    if (previewDetailsRef?.current) {
      previewDetailsRef.current = {
        ...previewDetailsRef.current,
        interviewersDetails,
        ats_office,
      };
    }
    // When formOnly, ignore form isValid for slot-only errors (we already filtered them); treat as valid if we built payload
    const effectiveValid = formOnly ? hasValidData : isValid && hasValidData;
    const result = { hasValidData: effectiveValid, payload };
    return result;
  };

  return { getSchedularDetailsModel };
};
